import { useState, useRef, useEffect } from 'react';

// Computed once (not per-card, per-render) — true for real touchscreens and
// for Chrome DevTools' device-emulation mode, which sets these same
// properties to simulate the device. `draggable` is gated on this being
// false: with it left unconditionally true, a touch-capable environment
// could fire native HTML5 drag (its own translucent browser-rendered drag
// image) *and* the custom touch long-press handler below for the same
// gesture — two overlapping, independently-positioned ghost visuals
// fighting each other, which is exactly what "glitching" during drag looks
// like. Genuine touch devices don't normally dispatch native drag from a
// touch gesture at all, but DevTools' emulation (real mouse input translated
// to synthetic touch events) can end up triggering both paths at once.
// Named export too — SearchAndCategories.js's click-and-drag-to-scroll tab
// strip needs the same real-touch-vs-mouse distinction, for the same
// reason: it should only take over the mouse path (native touch scrolling
// already works there without any help).
export const isTouchCapable = typeof window !== 'undefined' &&
  (('ontouchstart' in window) || (window.navigator?.maxTouchPoints > 0));

// Shared drag-and-drop mechanics for reorderable list rows — HTML5 drag
// events for desktop, plus a custom long-press/ghost-element/elementFromPoint
// implementation for touch devices (native HTML5 drag doesn't fire on touch).
// Originally built for GearCard.js; extracted so NeedItem/PackItem can reuse
// the exact same mechanics.
//
// The consumer renders its own card element and attaches `cardRef` plus
// spreads `dragHandlers` (and `dataAttrs`, used by the touch hit-test to find
// the row under the finger) onto it. `onDragStart`/`onDragOver`/`onDragEnd`
// are called with (event, id, name, category) — event is null for touch.
export default function useDragItem({ id, name, category, onDragStart, onDragOver, onDragEnd }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragEnterCount = useRef(0);

  const cardRef = useRef(null);
  const ghostRef = useRef(null);
  const touchDragging = useRef(false);
  const touchOffsetY = useRef(0);
  const touchOffsetX = useRef(0);
  const longPressTimer = useRef(null);
  const touchStartPos = useRef({ x: 0, y: 0 });
  // Last item id reported to onDragOver during this drag — touchmove fires
  // continuously (many times a second) while the finger sits over the same
  // card, and every consumer's onDragOver hands that straight to a plain
  // setState({...new object}); with no dedup, that re-renders the entire
  // list dozens of times a second for the whole drag, which is what reads
  // as "glitching". Only notify the consumer when the hovered card actually
  // changes.
  const lastOverIdRef = useRef(null);

  // Stable refs for callbacks used inside the long-press timer / touchmove
  // listener (avoid stale closures — those fire well after the render that
  // scheduled them).
  const onDragStartRef = useRef(onDragStart);
  const onDragOverRef = useRef(onDragOver);
  const onDragEndRef = useRef(onDragEnd);
  const idRef = useRef(id);
  const nameRef = useRef(name);
  const categoryRef = useRef(category);
  useEffect(() => { onDragStartRef.current = onDragStart; }, [onDragStart]);
  useEffect(() => { onDragOverRef.current = onDragOver; }, [onDragOver]);
  useEffect(() => { onDragEndRef.current = onDragEnd; }, [onDragEnd]);
  useEffect(() => { idRef.current = id; }, [id]);
  useEffect(() => { nameRef.current = name; }, [name]);
  useEffect(() => { categoryRef.current = category; }, [category]);

  // Passive-false touchmove listener (must be imperative — React attaches passive by default)
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const onTouchMove = (e) => {
      if (!touchDragging.current) {
        // Cancel long-press if finger moved significantly before timer fired
        const touch = e.touches[0];
        const dx = touch.clientX - touchStartPos.current.x;
        const dy = touch.clientY - touchStartPos.current.y;
        if (Math.sqrt(dx * dx + dy * dy) > 8) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
        }
        return;
      }
      e.preventDefault();
      const touch = e.touches[0];
      const ghost = ghostRef.current;
      if (ghost) {
        ghost.style.top = `${touch.clientY - touchOffsetY.current}px`;
        ghost.style.left = `${touch.clientX - touchOffsetX.current}px`;
      }
      // Hit-test the element under the finger (excluding the ghost)
      if (ghost) ghost.style.pointerEvents = 'none';
      const target = document.elementFromPoint(touch.clientX, touch.clientY);
      if (ghost) ghost.style.pointerEvents = '';
      const targetCard = target?.closest('[data-item-id]');
      if (targetCard) {
        const overId = targetCard.getAttribute('data-item-id');
        const overName = targetCard.getAttribute('data-item-name');
        const overCat = targetCard.getAttribute('data-item-category');
        if (overId && overId !== idRef.current && overId !== lastOverIdRef.current) {
          lastOverIdRef.current = overId;
          onDragOverRef.current && onDragOverRef.current(null, overId, overName, overCat);
        }
      }
    };
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMove);
  }, []);

  const handleTouchStart = (e) => {
    if (!onDragStartRef.current) return;
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };

    longPressTimer.current = setTimeout(() => {
      const rect = cardRef.current?.getBoundingClientRect();
      touchOffsetY.current = touchStartPos.current.y - (rect?.top ?? 0);
      touchOffsetX.current = touchStartPos.current.x - (rect?.left ?? 0);
      touchDragging.current = true;
      lastOverIdRef.current = null;

      // Create ghost clone
      const ghost = cardRef.current.cloneNode(true);
      ghost.style.cssText = `
        position: fixed;
        top: ${rect?.top ?? 0}px;
        left: ${rect?.left ?? 0}px;
        width: ${rect?.width ?? 0}px;
        opacity: 0.85;
        pointer-events: none;
        z-index: 9999;
        transform: scale(1.02);
        transition: none;
        border-radius: 16px;
        box-shadow: 0 8px 24px rgba(44,48,48,0.18);
      `;
      document.body.appendChild(ghost);
      ghostRef.current = ghost;

      setIsDragging(true);
      onDragStartRef.current(null, idRef.current, nameRef.current, categoryRef.current);
    }, 400);
  };

  const handleTouchEnd = () => {
    clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
    if (!touchDragging.current) return;
    touchDragging.current = false;
    if (ghostRef.current) {
      document.body.removeChild(ghostRef.current);
      ghostRef.current = null;
    }
    setIsDragging(false);
    setIsDragOver(false);
    onDragEndRef.current && onDragEndRef.current(null);
  };

  const dataAttrs = {
    'data-item-id': id,
    'data-item-name': name,
    'data-item-category': category || '',
  };

  const dragHandlers = {
    draggable: !!onDragStart && !isTouchCapable,
    onTouchStart: onDragStart ? handleTouchStart : undefined,
    onTouchEnd: onDragStart ? handleTouchEnd : undefined,
    onDragStart: (e) => {
      setIsDragging(true);
      onDragStart && onDragStart(e, id, name, category);
    },
    onDragEnter: () => {
      dragEnterCount.current += 1;
      setIsDragOver(true);
    },
    onDragOver: (e) => {
      e.preventDefault();
      onDragOver && onDragOver(e, id, name, category);
    },
    onDragLeave: () => {
      dragEnterCount.current -= 1;
      if (dragEnterCount.current <= 0) {
        dragEnterCount.current = 0;
        setIsDragOver(false);
      }
    },
    onDrop: () => {
      dragEnterCount.current = 0;
      setIsDragOver(false);
    },
    onDragEnd: (e) => {
      setIsDragging(false);
      setIsDragOver(false);
      dragEnterCount.current = 0;
      onDragEnd && onDragEnd(e);
    },
  };

  return { cardRef, isDragging, isDragOver, dragHandlers, dataAttrs };
}
